import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import {
  useCreateIncidentMutation,
  useGetUploadUrlMutation,
  useGetLocationStaffQuery,
  useGetEnrichedStudentsQuery,
  useGetIncidentFieldsQuery,
} from '../../store/api/localApi';
import {
  IncidentSeverity,
  IncidentType,
  NotifiableCategory,
  IncidentCustomFieldType,
  IncidentCustomFieldDef,
} from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../Toast';

/** Shared-narrative fields carried over when recording the SAME incident for
 *  a second child (spec §1, "record for another child"). Deliberately does
 *  NOT include childId/injuryDetail/actionTaken/attachments — those are
 *  per-child and must stay blank so the new record reflects what actually
 *  happened to THIS child, not a copy of the first child's specifics. */
export interface IncidentCreatePrefill {
  incidentGroupId: string;
  occurredAt: string;
  locationDetail: string;
  description: string;
  incidentType: IncidentType;
  severity: IncidentSeverity;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Known-child mode (e.g. RunSession's roster ⚠ button): the child is
   *  fixed, no picker is shown. Omit both to enter child-SELECT mode
   *  instead (the "record for another child" flow) — a dropdown appears
   *  (`incident-child-select`) sourced from the location's roster, and the
   *  submit stays disabled until a child is chosen. */
  studentId?: string;
  studentName?: string;
  locationId: string;
  organizationId: string;
  /** The live session's class name — used only as the default, editable
   *  `locationDetail` value (see DEFAULT note below). */
  className?: string;
  /** The live session this incident is being filed from, when launched off a
   *  running session's roster. Sent to the backend as an optional context link
   *  so the session's activity summary can list it. Absent for the standalone
   *  "record an incident" flow from the register. */
  sessionId?: string;
  /** Pre-fills the shared narrative fields (see IncidentCreatePrefill) —
   *  used only by the "record for another child" flow off the detail page.
   *  Absent for the normal RunSession launch. */
  prefill?: IncidentCreatePrefill;
}

const TEXT_LIMIT = 2000;

/**
 * `incidentType` and `locationDetail` default on open to keep the minor path
 * (severity → description → submit) at 3 interactions without a wizard step
 * — `incidentSubmitSchema` (src/incidents/utils/schemas.ts) requires both
 * non-empty for ANY `submitNow: true` call, regardless of severity. Both
 * defaults remain visible and editable before submit, and both are honest,
 * not placeholders:
 *   - incidentType defaults to OTHER (a legitimate category, not a "TBD").
 *   - locationDetail defaults to the session's class name (true — that IS
 *     where the incident was reported from), overridable via the input.
 *
 * `actionTaken` is deliberately NOT defaulted (fix round 1, product-owner
 * call): it is `.optional()` in incidentSubmitSchema, matching
 * `injuryDetail`'s treatment one line above — plenty of minor incidents
 * genuinely have no action beyond comfort and observation, and a canned
 * "no action recorded" sentence on every fast-path MINOR incident would read
 * as a UI artifact rather than a staff observation in a 2-year statutory
 * register. It stays visible inside "Add more detail" and simply starts
 * empty; staff who want to record something still can.
 */
const LOCATION_DETAIL_LIMIT = 200;
const MAX_ATTACHMENTS = 6;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB — matches PURPOSE_CONFIG[INCIDENT_ATTACHMENT]
const ATTACHMENT_MIME_TYPES = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';

const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  [IncidentType.ACCIDENT]: 'Accident',
  [IncidentType.INJURY]: 'Injury',
  [IncidentType.ILLNESS]: 'Illness',
  [IncidentType.MEDICATION_ERROR]: 'Medication error',
  [IncidentType.BEHAVIOURAL]: 'Behavioural',
  [IncidentType.OTHER]: 'Other',
};

/**
 * Plain-English helper text for each statutory Tusla category (S.I.
 * 575/2018). A staff member recording this in the moment is not a
 * regulatory expert — the legal label alone ("serious injury requiring
 * hospital treatment") does not tell them whether tonight's A&E visit for
 * stitches qualifies. Every option needs a concrete, recognisable example.
 */
const NOTIFIABLE_CATEGORY_HELP: Record<NotifiableCategory, { label: string; help: string }> = {
  [NotifiableCategory.SERIOUS_INJURY_HOSPITAL]: {
    label: 'Serious injury requiring hospital treatment',
    help: 'Includes A&E visits for stitches or a suspected fracture.',
  },
  [NotifiableCategory.INFECTIOUS_DISEASE]: {
    label: 'Notifiable infectious disease',
    help: 'A disease a doctor is legally required to report (e.g. measles, meningitis).',
  },
  [NotifiableCategory.CHILD_MISSING]: {
    label: 'Child missing or unaccounted for',
    help: 'A child could not be located during the session, even briefly.',
  },
  [NotifiableCategory.UNPLANNED_CLOSURE]: {
    label: 'Unplanned closure of the service',
    help: 'The service had to shut early or for the day without prior notice.',
  },
  [NotifiableCategory.DEATH]: {
    label: 'Death of a child while in service',
    help: 'The most serious category — notify Tusla and management immediately by phone.',
  },
  [NotifiableCategory.OTHER_NOTIFIABLE]: {
    label: 'Other notifiable event',
    help: 'Any other event Tusla requires to be reported that isn’t listed above.',
  },
};

interface UploadingAttachment {
  s3Key: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl?: string;
  fileName: string;
  uploading: boolean;
  error?: string;
}

/**
 * Incident create modal — launched from the RunSession roster's ⚠ button.
 *
 * Mirrors ObservationCreateModal's shape deliberately: single flat
 * `role="dialog"`, everything visible at once, child preselected from props
 * (never a lookup), Escape/backdrop close, state resets on open. This is NOT
 * a wizard — the spec's under-2-minutes claim depends on the minor path
 * being severity → description → submit, three interactions, enforced by
 * `tests/e2e/incidents-create.spec.ts` counting clicks, not asserted in a
 * comment.
 *
 * Progressive disclosure, driven entirely by severity:
 *   - Always visible: severity (no default — a deliberate choice is forced,
 *     since this field can start a legal Tusla clock), description,
 *     occurredAt, incidentType chips, locationDetail.
 *   - SIGNIFICANT/NOTIFIABLE: witness picker becomes required.
 *   - NOTIFIABLE only: notifiableCategory becomes required, with
 *     plain-English helper text under each option.
 *   - ACCIDENT/INJURY: injuryDetail appears.
 *   - "Add more detail" disclosure (actionTaken, followUp, attachments):
 *     collapsed by default, expanded by default ONLY for NOTIFIABLE.
 */
const IncidentCreateModal: React.FC<Props> = ({
  open,
  onClose,
  studentId,
  studentName,
  locationId,
  organizationId,
  className,
  sessionId,
  prefill,
}) => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [createIncident, { isLoading: isSaving }] = useCreateIncidentMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();
  const { data: staffData } = useGetLocationStaffQuery({ locationId }, { skip: !open });
  // Org-configured custom fields (Task 24) — org-scoped, uniform across the
  // org's locations, so keyed on organizationId not locationId.
  const { data: fieldsData } = useGetIncidentFieldsQuery({ organizationId }, { skip: !open });
  const customFieldDefs = [...(fieldsData?.config.fields ?? [])].sort((a, b) => a.order - b.order);

  // Child-SELECT mode: no studentId/studentName prop given (the "record for
  // another child" flow from the incident detail page). Sourced from the
  // same location roster query RunSession itself already uses.
  const needsChildPicker = !studentId;
  const { data: locationStudentsData } = useGetEnrichedStudentsQuery(
    { locationId },
    { skip: !open || !needsChildPicker }
  );
  const [selectedChildId, setSelectedChildId] = useState('');
  // Don't offer a departed child in the new-incident picker — the students-list
  // default widened to include LEFT children (Task 18), and you can't raise a
  // fresh incident against a child who has left. History for a LEFT child is
  // still reachable from their record.
  const childCandidates = (locationStudentsData?.students ?? [])
    .filter((s) => s.status !== 'LEFT')
    .map((s) => ({
      id: s.id,
      name: `${s.firstName} ${s.lastName}`.trim(),
    }));
  // Resolved child id/name: the fixed prop in known-child mode, or the
  // picker's current selection in child-SELECT mode.
  const effectiveChildId = studentId ?? selectedChildId;
  const effectiveChildName =
    studentName ?? childCandidates.find((c) => c.id === selectedChildId)?.name ?? '';

  const [incidentId, setIncidentId] = useState('');
  const [incidentGroupId, setIncidentGroupId] = useState<string | undefined>(undefined);
  const [severity, setSeverity] = useState<IncidentSeverity | null>(null);
  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [incidentType, setIncidentType] = useState<IncidentType | null>(null);
  const [locationDetail, setLocationDetail] = useState('');
  const [witnessStaffId, setWitnessStaffId] = useState('');
  const [notifiableCategory, setNotifiableCategory] = useState<NotifiableCategory | null>(null);
  const [injuryDetail, setInjuryDetail] = useState('');
  const [showMoreDetail, setShowMoreDetail] = useState(false);
  const [actionTaken, setActionTaken] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [attachments, setAttachments] = useState<UploadingAttachment[]>([]);
  const [witnessError, setWitnessError] = useState(false);
  const [notifiableCategoryError, setNotifiableCategoryError] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<Set<string>>(new Set());

  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Injecting the current user into the witness list, same trick as
  // StartDrillModal — an admin filling in for staff may not appear in the
  // location's own staff roster.
  const witnessCandidates = (() => {
    const list = (staffData?.staff || []).map((s) => ({
      id: s.id,
      name: `${s.firstName} ${s.lastName}`.trim(),
    }));
    if (user && !list.some((m) => m.id === user.id)) {
      list.unshift({ id: user.id, name: `${user.firstName} ${user.lastName}`.trim() });
    }
    return list;
  })();

  const needsWitness =
    severity === IncidentSeverity.SIGNIFICANT || severity === IncidentSeverity.NOTIFIABLE;
  const isNotifiable = severity === IncidentSeverity.NOTIFIABLE;
  const needsInjuryDetail =
    incidentType === IncidentType.ACCIDENT || incidentType === IncidentType.INJURY;

  // Reset all state on open — including generating a FRESH client-side
  // incidentId. Generated BEFORE any attachment upload so the upload's
  // presign contextId (`${incidentId}:${locationId}`) is stable for the
  // whole modal lifetime, and the same id is idempotent-submitted on
  // POST /incidents.
  //
  // `prefill` (record-for-another-child flow) seeds the shared narrative
  // fields — severity, description, occurredAt, locationDetail,
  // incidentType, incidentGroupId — leaving childId/injuryDetail/
  // actionTaken/attachments blank, per spec §1. Everything it sets remains
  // editable, same as the className-derived locationDetail default below.
  useEffect(() => {
    if (open) {
      setIncidentId(uuidv4());
      setIncidentGroupId(prefill?.incidentGroupId);
      setSeverity(prefill?.severity ?? null);
      setDescription(prefill?.description ?? '');
      setOccurredAt(
        prefill?.occurredAt
          ? toLocalDateTimeInputValue(new Date(prefill.occurredAt))
          : toLocalDateTimeInputValue(new Date())
      );
      // incidentType/locationDetail defaults satisfy incidentSubmitSchema's
      // non-empty requirement on the minor path without a wizard step — see
      // the doc comment above. Both remain visible and editable before
      // submit. actionTaken deliberately starts empty (optional at submit,
      // same as injuryDetail) — no canned "nothing recorded" text.
      setIncidentType(prefill?.incidentType ?? IncidentType.OTHER);
      setLocationDetail(prefill?.locationDetail ?? className ?? '');
      setSelectedChildId('');
      setWitnessStaffId('');
      setNotifiableCategory(null);
      setInjuryDetail('');
      setShowMoreDetail(false);
      setActionTaken('');
      setFollowUp('');
      setAttachments([]);
      setWitnessError(false);
      setCustomFieldValues({});
      setCustomFieldErrors(new Set());
      closeBtnRef.current?.focus();
    }
  }, [open, studentId, className, prefill]);

  // NOTIFIABLE expands "Add more detail" by default — a statutory incident
  // almost always needs actionTaken/followUp filled in immediately. Any
  // other severity leaves it collapsed so the minor path stays 3 taps.
  useEffect(() => {
    if (severity === IncidentSeverity.NOTIFIABLE) setShowMoreDetail(true);
  }, [severity]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const anyUploading = attachments.some((a) => a.uploading);

  // Minor-path gate: severity + description is ALL the SUBMIT BUTTON's
  // enabled-state depends on. Witness / notifiableCategory are deliberately
  // NOT part of this gate — they surface as inline validation errors ON
  // CLICK instead (see handleSubmit), matching the brief's expectation that
  // a NOTIFIABLE submit attempt without a witness produces a visible error
  // rather than a silently-disabled button. This is what keeps the minor
  // path at exactly 3 interactions (severity tap, description fill, submit)
  // while still catching the conditional requirements for higher severities.
  // In child-SELECT mode a chosen child is ALSO required — unlike
  // witness/notifiableCategory this has no meaningful "submit then error"
  // recovery (there's no child to attach the record to at all).
  const canSubmit =
    severity !== null &&
    description.trim().length > 0 &&
    !!effectiveChildId &&
    !anyUploading &&
    !isSaving;

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const slots = MAX_ATTACHMENTS - attachments.length;
    const fresh = Array.from(files).slice(0, slots);

    for (const file of fresh) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        showError(`${file.name} is over the 10MB limit.`);
        continue;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      const next: UploadingAttachment = {
        s3Key: '',
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
        fileName: file.name,
        uploading: true,
      };
      setAttachments((prev) => [...prev, next]);
      try {
        // contextId is `${incidentId}:${locationId}` — incidentId FIRST, a
        // literal colon, then locationId. The presign authorizer splits on
        // the FIRST colon only; this only works because both halves are
        // UUIDs. Reversed order or a different separator misparses.
        const { uploadUrl, s3Key } = await getUploadUrl({
          purpose: 'INCIDENT_ATTACHMENT',
          contextId: `${incidentId}:${locationId}`,
          mimeType: file.type,
          sizeBytes: file.size,
          fileName: file.name,
        }).unwrap();
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
        setAttachments((prev) =>
          prev.map((a) =>
            a.fileName === next.fileName && a.uploading ? { ...a, s3Key, uploading: false } : a
          )
        );
      } catch {
        setAttachments((prev) =>
          prev.map((a) =>
            a.fileName === next.fileName && a.uploading
              ? { ...a, uploading: false, error: 'Failed' }
              : a
          )
        );
      }
    }
  };

  const buildPayload = (submitNow: boolean) => ({
    incidentId,
    childId: effectiveChildId,
    locationId,
    organizationId,
    incidentGroupId,
    sessionId,
    incidentType: incidentType ?? undefined,
    occurredAt: occurredAt ? new Date(occurredAt).toISOString() : undefined,
    locationDetail: locationDetail.trim() || undefined,
    description: description.trim() || undefined,
    injuryDetail: needsInjuryDetail ? injuryDetail.trim() || undefined : undefined,
    actionTaken: actionTaken.trim() || undefined,
    witnessStaffId: witnessStaffId || undefined,
    severity: severity ?? undefined,
    notifiableCategory: isNotifiable ? notifiableCategory ?? undefined : undefined,
    followUp: followUp.trim() || undefined,
    attachments: attachments
      .filter((a) => a.s3Key && !a.error)
      .map(({ s3Key, mimeType, sizeBytes }) => ({ s3Key, mimeType, sizeBytes })),
    customFields: customFieldValues,
    submitNow,
  });

  // Client-side mirror of validate-custom-fields.ts's required-ness check —
  // only enforced on SUBMIT, never on a saved DRAFT (same principle as
  // witness/notifiableCategory below, and the server-side enforceRequired
  // flag this mirrors). Returns the set of field keys missing a required
  // answer; empty means valid.
  const missingRequiredCustomFields = (): Set<string> => {
    const missing = new Set<string>();
    for (const f of customFieldDefs) {
      if (!f.required) continue;
      const v = customFieldValues[f.key];
      const isEmpty =
        v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
      if (isEmpty) missing.add(f.key);
    }
    return missing;
  };

  const handleSubmit = async () => {
    if (isSaving || anyUploading) return;
    setWitnessError(false);
    setNotifiableCategoryError(false);
    // Client-side mirror of incidentSubmitSchema's conditional rules (see
    // src/incidents/utils/schemas.ts) — checked before the round trip so the
    // error surfaces instantly, with the server's rejection codes as a
    // fallback below in case either rule ever drifts out of sync.
    if (needsWitness && !witnessStaffId) {
      setWitnessError(true);
      return;
    }
    if (isNotifiable && !notifiableCategory) {
      setNotifiableCategoryError(true);
      return;
    }
    const missingCustom = missingRequiredCustomFields();
    if (missingCustom.size > 0) {
      setCustomFieldErrors(missingCustom);
      // Custom fields render inside "Add more detail", which starts
      // collapsed for MINOR/SIGNIFICANT — without this, a blocked submit
      // would leave the error invisible behind the collapsed panel.
      setShowMoreDetail(true);
      return;
    }
    try {
      const result = await createIncident(buildPayload(true)).unwrap();
      const n = result.recipientCount;
      showSuccess(
        n > 0
          ? `Incident recorded — sent to ${n} parent${n === 1 ? '' : 's'}.`
          : 'Incident recorded.',
        'incident-created-toast'
      );
      onClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      if (data?.error === 'WITNESS_REQUIRED') {
        setWitnessError(true);
        return;
      }
      if (data?.error === 'NOTIFIABLE_CATEGORY_REQUIRED') {
        setNotifiableCategoryError(true);
        return;
      }
      showError(data?.error || data?.message || 'Failed to record incident.');
    }
  };

  const handleSaveDraft = async () => {
    if (isSaving || anyUploading) return;
    try {
      await createIncident(buildPayload(false)).unwrap();
      showSuccess('Draft saved.');
      onClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to save draft.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="incident-create-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incident-create-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          data-testid="incident-create-backdrop"
          aria-hidden="true"
        />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 id="incident-create-title" className="text-lg font-semibold text-gray-900">
              Record incident{effectiveChildName ? ` — ${effectiveChildName}` : ''}
            </h3>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              data-testid="incident-create-cancel"
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Child picker — only in child-SELECT mode ("record for another
              child" off the incident detail page). RunSession's known-child
              launch never renders this; the child is fixed via props. */}
          {needsChildPicker && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Child <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="incident-child-select"
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                className="block w-full rounded-md border-gray-300 text-sm"
              >
                <option value="">Select a child…</option>
                {childCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Severity — three large tap targets, NO default. A deliberate
              choice is forced here because this field can start a Tusla
              legal clock (NOTIFIABLE). */}
          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-gray-700 mb-1.5">
              Severity <span className="text-red-500">*</span>
            </legend>
            <div className="grid grid-cols-3 gap-2">
              <SeverityButton
                severity={IncidentSeverity.MINOR}
                label="Minor"
                selected={severity === IncidentSeverity.MINOR}
                onClick={() => setSeverity(IncidentSeverity.MINOR)}
                classes="border-emerald-300 data-[selected=true]:bg-emerald-600 data-[selected=true]:text-white data-[selected=true]:border-emerald-600 text-emerald-700"
              />
              <SeverityButton
                severity={IncidentSeverity.SIGNIFICANT}
                label="Significant"
                selected={severity === IncidentSeverity.SIGNIFICANT}
                onClick={() => setSeverity(IncidentSeverity.SIGNIFICANT)}
                classes="border-amber-300 data-[selected=true]:bg-amber-600 data-[selected=true]:text-white data-[selected=true]:border-amber-600 text-amber-700"
              />
              <SeverityButton
                severity={IncidentSeverity.NOTIFIABLE}
                label="Notifiable"
                selected={severity === IncidentSeverity.NOTIFIABLE}
                onClick={() => setSeverity(IncidentSeverity.NOTIFIABLE)}
                classes="border-rose-300 data-[selected=true]:bg-rose-600 data-[selected=true]:text-white data-[selected=true]:border-rose-600 text-rose-700"
              />
            </div>
          </fieldset>

          {/* Description — always visible */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              What happened <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="incident-description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, TEXT_LIMIT))}
              placeholder={`Describe what happened${
                effectiveChildName ? ` to ${effectiveChildName}` : ''
              }…`}
              className="block w-full rounded-md border-gray-300 h-24 resize-y"
            />
          </div>

          {/* Occurred at — always visible, defaults to now */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">When</label>
            <input
              type="datetime-local"
              data-testid="incident-occurred-at"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="block w-full rounded-md border-gray-300 text-sm"
            />
          </div>

          {/* Incident type chips — always visible */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.values(IncidentType).map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`incident-type-${t}`}
                  onClick={() => setIncidentType(t)}
                  aria-pressed={incidentType === t}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    incidentType === t
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {INCIDENT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Location detail — always visible */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Where</label>
            <input
              type="text"
              data-testid="incident-location-detail"
              value={locationDetail}
              onChange={(e) => setLocationDetail(e.target.value.slice(0, LOCATION_DETAIL_LIMIT))}
              placeholder="e.g. Yard, near the climbing frame"
              className="block w-full rounded-md border-gray-300 text-sm"
            />
          </div>

          {/* Witness — conditional: SIGNIFICANT/NOTIFIABLE only */}
          {needsWitness && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Witness <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="incident-witness-select"
                value={witnessStaffId}
                onChange={(e) => {
                  setWitnessStaffId(e.target.value);
                  if (e.target.value) setWitnessError(false);
                }}
                className="block w-full rounded-md border-gray-300 text-sm"
              >
                <option value="">Select a witness…</option>
                {witnessCandidates.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.id === user?.id ? ' (you)' : ''}
                  </option>
                ))}
              </select>
              {witnessError && (
                <p className="mt-1 text-xs text-red-600" data-testid="incident-witness-error">
                  A witness is required for{' '}
                  {severity === IncidentSeverity.NOTIFIABLE ? 'a notifiable' : 'a significant'}{' '}
                  incident.
                </p>
              )}
            </div>
          )}

          {/* Notifiable category — conditional: NOTIFIABLE only, plain-English helper text */}
          {isNotifiable && (
            <fieldset className="mb-3" data-testid="incident-notifiable-category">
              <legend className="text-sm font-medium text-gray-700 mb-1.5">
                Notifiable category <span className="text-red-500">*</span>
              </legend>
              <div className="space-y-2 max-h-56 overflow-y-auto border border-gray-200 rounded-md p-2">
                {Object.entries(NOTIFIABLE_CATEGORY_HELP).map(([value, cfg]) => (
                  <label
                    key={value}
                    className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="notifiableCategory"
                      data-testid={`incident-notifiable-category-${value}`}
                      checked={notifiableCategory === value}
                      onChange={() => {
                        setNotifiableCategory(value as NotifiableCategory);
                        setNotifiableCategoryError(false);
                      }}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm text-gray-900 font-medium">{cfg.label}</span>
                      <span className="block text-xs text-gray-500">{cfg.help}</span>
                    </span>
                  </label>
                ))}
              </div>
              {notifiableCategoryError && (
                <p
                  className="mt-1 text-xs text-red-600"
                  data-testid="incident-notifiable-category-error"
                >
                  A notifiable category is required for a notifiable incident.
                </p>
              )}
            </fieldset>
          )}

          {/* Injury detail — conditional: ACCIDENT/INJURY only */}
          {needsInjuryDetail && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Injury detail</label>
              <textarea
                data-testid="incident-injury-detail"
                value={injuryDetail}
                onChange={(e) => setInjuryDetail(e.target.value.slice(0, TEXT_LIMIT))}
                placeholder="Where on the body, what it looked like…"
                className="block w-full rounded-md border-gray-300 h-16 resize-y text-sm"
              />
            </div>
          )}

          {/* Add more detail — collapsed by default, expanded by default only for NOTIFIABLE */}
          <div className="mb-4 border-t border-gray-100 pt-3">
            <button
              type="button"
              data-testid="incident-more-detail-toggle"
              onClick={() => setShowMoreDetail((v) => !v)}
              aria-expanded={showMoreDetail}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {showMoreDetail ? '− Hide more detail' : '+ Add more detail'}
            </button>

            {showMoreDetail && (
              <div className="mt-3 space-y-3" data-testid="incident-more-detail-panel">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action taken
                  </label>
                  <textarea
                    data-testid="incident-action-taken"
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value.slice(0, TEXT_LIMIT))}
                    placeholder="What was done immediately afterwards…"
                    className="block w-full rounded-md border-gray-300 h-16 resize-y text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up</label>
                  <textarea
                    data-testid="incident-follow-up"
                    value={followUp}
                    onChange={(e) => setFollowUp(e.target.value.slice(0, TEXT_LIMIT))}
                    placeholder="Any follow-up needed…"
                    className="block w-full rounded-md border-gray-300 h-16 resize-y text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attachments
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {attachments.map((a, i) => (
                      <div
                        key={`${a.fileName}-${i}`}
                        data-testid={`incident-attachment-${i}`}
                        className="relative h-16 w-16 rounded-md overflow-hidden border border-gray-200 flex items-center justify-center bg-gray-50 text-[10px] text-gray-500 text-center px-1"
                      >
                        {a.previewUrl ? (
                          <img src={a.previewUrl} alt="" className="object-cover w-full h-full" />
                        ) : (
                          <span className="truncate">{a.fileName}</span>
                        )}
                        {a.uploading && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                        {a.error && (
                          <div className="absolute inset-0 bg-red-500/70 flex items-center justify-center text-[10px] text-white">
                            {a.error}
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid={`incident-attachment-${i}-remove`}
                          onClick={() => {
                            if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
                            setAttachments((prev) => prev.filter((_, idx) => idx !== i));
                          }}
                          className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5"
                          aria-label="Remove attachment"
                        >
                          <XMarkIcon className="h-3 w-3 text-gray-700" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {attachments.length < MAX_ATTACHMENTS && (
                    <label
                      data-testid="incident-add-attachment"
                      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      <PhotoIcon className="h-4 w-4" />
                      Add attachment
                      <input
                        type="file"
                        accept={ATTACHMENT_MIME_TYPES}
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                      />
                    </label>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {attachments.length} / {MAX_ATTACHMENTS} · up to 10MB each
                  </p>
                </div>

                {/* Org-configured custom fields (Task 24) — stored/displayed/
                    printed only, no workflow logic. Rendered in whatever
                    order the org configured (customFieldDefs is pre-sorted). */}
                {customFieldDefs.length > 0 && (
                  <div
                    className="space-y-3 border-t border-gray-100 pt-3"
                    data-testid="incident-custom-fields"
                  >
                    {customFieldDefs.map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        <CustomFieldInput
                          field={f}
                          value={customFieldValues[f.key]}
                          onChange={(v) => {
                            setCustomFieldValues((prev) => ({ ...prev, [f.key]: v }));
                            if (customFieldErrors.has(f.key)) {
                              setCustomFieldErrors((prev) => {
                                const next = new Set(prev);
                                next.delete(f.key);
                                return next;
                              });
                            }
                          }}
                        />
                        {customFieldErrors.has(f.key) && (
                          <p
                            className="mt-1 text-xs text-red-600"
                            data-testid={`incident-custom-field-error-${f.key}`}
                          >
                            {f.label} is required.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="incident-create-close-secondary"
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="incident-save-draft-btn"
              onClick={handleSaveDraft}
              disabled={isSaving || anyUploading}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save as Draft
            </button>
            <button
              type="button"
              data-testid="incident-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving…' : 'Submit incident'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SeverityButton: React.FC<{
  severity: IncidentSeverity;
  label: string;
  selected: boolean;
  onClick: () => void;
  classes: string;
}> = ({ severity, label, selected, onClick, classes }) => (
  <button
    type="button"
    data-testid={`incident-severity-${severity}`}
    onClick={onClick}
    data-selected={selected}
    aria-pressed={selected}
    className={`py-3 rounded-md border-2 text-sm font-semibold transition ${classes} ${
      selected ? '' : 'bg-white'
    }`}
  >
    {label}
  </button>
);

/**
 * Renders ONE org-configured custom field by type. Deliberately generic and
 * dumb — this component carries NO workflow logic, matching the design
 * intent for customFields as a whole (stored/displayed/printed, nothing
 * else). `value`/`onChange` are the raw answer value stored under
 * `customFields[field.key]`.
 */
const CustomFieldInput: React.FC<{
  field: IncidentCustomFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ field, value, onChange }) => {
  const testId = `incident-custom-field-${field.key}`;
  const options = field.options ?? [];

  switch (field.type) {
    case IncidentCustomFieldType.TEXTAREA:
      return (
        <textarea
          data-testid={testId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border-gray-300 h-16 resize-y text-sm"
        />
      );
    case IncidentCustomFieldType.NUMBER:
      return (
        <input
          type="number"
          data-testid={testId}
          value={(value as number | string) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className="block w-full rounded-md border-gray-300 text-sm"
        />
      );
    case IncidentCustomFieldType.DATE:
      return (
        <input
          type="date"
          data-testid={testId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border-gray-300 text-sm"
        />
      );
    case IncidentCustomFieldType.CHECKBOX:
      return (
        <input
          type="checkbox"
          data-testid={testId}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="rounded border-gray-300"
        />
      );
    case IncidentCustomFieldType.SELECT:
      return (
        <select
          data-testid={testId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border-gray-300 text-sm"
        >
          <option value="">Select…</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case IncidentCustomFieldType.RADIO:
      return (
        <div className="space-y-1">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="radio"
                data-testid={`${testId}-${opt}`}
                name={testId}
                checked={value === opt}
                onChange={() => onChange(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    case IncidentCustomFieldType.MULTI_SELECT: {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="space-y-1">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                data-testid={`${testId}-${opt}`}
                checked={selected.includes(opt)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt)
                  )
                }
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    case IncidentCustomFieldType.TEXT:
    default:
      return (
        <input
          type="text"
          data-testid={testId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full rounded-md border-gray-300 text-sm"
        />
      );
  }
};

/** `datetime-local` inputs need "YYYY-MM-DDTHH:mm" in LOCAL time, not the
 *  UTC ISO string `Date#toISOString()` produces — using toISOString()
 *  directly here would silently shift the displayed default time by the
 *  browser's UTC offset. */
function toLocalDateTimeInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export default IncidentCreateModal;
